import React, { memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import classNames from 'classnames';
import { useIsScrolled } from '@saucelabs/sl-util-helpers';

import JobCommandItemV2 from './JobCommandItemV2';
import JobMessageItem from './JobMessageItem';
import RootCauseBannerV2 from './root-cause/RootCauseBannerV2';
import { functions } from './../../webdriver-commands';
import { selectors } from './../../ducks/job';
import { operations } from './../../ducks/container';
import { useKeyboardNav } from './../hooks/useKeyboardNav';

const MemoizedCommandItem = memo(JobCommandItemV2);

const SessionStoppedFooter = () => (
  <div
    className="flex top-0 bg-white text-base pt-1 pb-1 text-center text-gray-600 items-center"
  >
    <div className="flex-grow bg-gray-300 h-px mr-2" />
    Session stopped
    <div className="flex-grow bg-gray-300 h-px ml-2" />
  </div>
);

const JobCommandList = ({
  auth,
  autoFocus,
  commands,
  getPerformanceURL,
  getScreenshotUrl,
  handleAnalysisBtnClick,
  handleCommandClick,
  handleCommandIconClick,
  handlePerformanceClick,
  hideDuration,
  id,
  isAdvanceDisabled,
  onFailedShare,
  onSuccessfulShare,
  screenshotsMapping,
  selectorsMapping,
  showPerformance,
  rootCauseName,
  stopped,
}) => {
  const commandsSelectorsMap = selectorsMapping || functions.buildSeleniumSelectorsMap(commands);
  const commandWithRootCause = commands.find((command) => !!command.get('rootCauseType'));
  const rootCauseRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const { isScrolled } = useIsScrolled(containerRef);

  const activeCommand = useSelector(selectors.selectActiveCommand);
  const expandedCommands = useSelector(selectors.selectExpandedCommands);

  const expandedIds = expandedCommands.map((command) => command?.get('id')).toSet();

  const selectableCommands = commands.filter((command) => !command.message);

  const dispatch = useDispatch();
  const { keyboardProps } = useKeyboardNav({
    autoFocus,
    ref: containerRef,
    onNext: () => {
      const currIndex = selectableCommands.findIndex((command) => command.get('id') === activeCommand?.get('id'));
      const nextIndex = currIndex + 1 >= selectableCommands.size ? 0 : currIndex + 1;
      const nextCommand = selectableCommands.get(nextIndex);

      handleCommandClick(nextCommand);
    },
    onPrev: () => {
      const currIndex = selectableCommands.findIndex((command) => command.get('id') === activeCommand?.get('id'));
      const prevIndex = currIndex - 1 < 0 ? commands.size - 1 : currIndex - 1;
      const prevCommand = selectableCommands.get(prevIndex);

      handleCommandClick(prevCommand);
    },
    onToggle: () => {
      dispatch(operations.videoPlayPause());
    },
  });
  // Scrollbar from sl-component scrollbar isn't used here because scrolling to commands
  // via react-scroll doesn't work with it
  return (
    <div
      {...keyboardProps}
      ref={containerRef}
      className="h-full w-full relative overflow-auto"
      id={id}
      role="grid"
      tabIndex="0"
    >
      <div
        className={classNames('sticky -top-1 -mt-1 z-50 w-full transition-opacity linear h-1 shadow-md opacity-0', {
          'opacity-100': isScrolled,
        })}
      />
      {(!!commandWithRootCause) && (
        <RootCauseBannerV2
          command={commandWithRootCause}
          onButtonClick={handleAnalysisBtnClick}
          seleniumSelector={commandsSelectorsMap[commandWithRootCause.getSeleniumId()]}
        />
      )}

      {commands.map((command) => (
        command.message ? (
          <JobMessageItem
            key={command.id}
            command={command}
          />
        ) : (
          <MemoizedCommandItem
            key={command.id}
            active={command.id === activeCommand?.id}
            auth={auth}
            collapsed={!expandedIds.has(command.id)}
            command={command}
            getPerformanceURL={getPerformanceURL}
            getScreenshotUrl={getScreenshotUrl}
            handleAnalysisBtnClick={handleAnalysisBtnClick}
            handleClick={handleCommandClick}
            handleIconClick={handleCommandIconClick}
            handlePerformanceClick={handlePerformanceClick}
            hideDuration={hideDuration}
            isAdvanceDisabled={isAdvanceDisabled}
            name={`command-${command.id}`}
            onFailedShare={onFailedShare}
            onSuccessfulShare={onSuccessfulShare}
            rootCauseName={rootCauseName}
            rootCauseRef={rootCauseRef}
            screenshotsMapping={screenshotsMapping}
            seleniumSelector={commandsSelectorsMap[command.getSeleniumId()]}
            showPerformance={showPerformance}
          />
        )
      ))}
      {stopped && <SessionStoppedFooter />}
    </div>
  );
};

JobCommandList.propTypes = {
  auth: PropTypes.string, // HMAC auth param
  autoFocus: PropTypes.bool,
  commands: ImmutablePropTypes.list.isRequired,
  getPerformanceURL: PropTypes.func,
  getScreenshotUrl: PropTypes.func.isRequired,
  handleAnalysisBtnClick: PropTypes.func,
  handleCommandClick: PropTypes.func,
  handleCommandIconClick: PropTypes.func,
  handlePerformanceClick: PropTypes.func,
  hideDuration: PropTypes.bool,
  id: PropTypes.string,
  isAdvanceDisabled: PropTypes.bool,
  onFailedShare: PropTypes.func.isRequired,
  onSuccessfulShare: PropTypes.func.isRequired,
  rootCauseName: PropTypes.string,
  screenshotsMapping: PropTypes.array,
  selectorsMapping: PropTypes.object,
  showPerformance: PropTypes.bool,
  stopped: PropTypes.bool,
};

JobCommandList.defaultProps = {
  auth: undefined,
  autoFocus: false,
  getPerformanceURL: null,
  handleAnalysisBtnClick: () => null,
  handleCommandClick: () => null,
  handleCommandIconClick: () => null,
  handlePerformanceClick: () => null,
  hideDuration: false,
  id: null,
  isAdvanceDisabled: false,
  rootCauseName: null,
  screenshotsMapping: [],
  selectorsMapping: null,
  showPerformance: true,
  stopped: false,
};

export default JobCommandList;
export {
  JobCommandList,
};
